
import { useRef, useState, useEffect, useMemo } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";

export default function Application() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);

  // Optimize the observer by using a more efficient callback and options
  useEffect(() => {
    let observerRefValue = sectionRef.current;
    
    // Use the same observer for all instances
    const observer = new IntersectionObserver(
      (entries) => {
        // Process only once when it becomes visible
        if (entries[0].isIntersecting && !isVisible) {
          setIsVisible(true);
          observer.disconnect(); // Immediately disconnect after triggering
        }
      },
      {
        root: null,
        threshold: 0.1, // Lower threshold for earlier detection
        rootMargin: "0px" // Default margin
      }
    );

    if (observerRefValue) {
      observer.observe(observerRefValue);
    }

    return () => {
      if (observerRefValue) {
        observer.unobserve(observerRefValue);
      }
      observer.disconnect();
    };
  }, [isVisible]); // Only re-run if visibility state changes

  // Use a more efficient approach for animation timing
  useEffect(() => {
    if (!isVisible) return;
    
    // Use a single timeout and state update instead of multiple timeouts
    const timeout = setTimeout(() => {
      setActiveItemIndex(0); // Start the sequence
      
      // Use requestAnimationFrame for smoother animations
      const animate = (index: number, delay: number) => {
        if (index > 3) return; // Stop at 4 items
        
        setTimeout(() => {
          setActiveItemIndex(index);
          requestAnimationFrame(() => animate(index + 1, 400)); // Use consistent timing
        }, delay);
      };
      
      animate(1, 400); // Start after the first item
    }, 600);
    
    return () => clearTimeout(timeout);
  }, [isVisible]);

  // Memoize the list items to prevent unnecessary re-renders
  const listItems = useMemo(() => [
    "Mental clarity in a marketplace of noise.",
    "Purpose that transcends your performance metrics.",
    "Freedom from the constant need for external validation.",
    "Leadership flowing powerfully from harmony between head and heart."
  ], []);

  return (
    <section
      ref={sectionRef}
      className="py-16 sm:py-20 md:py-28 bg-offwhite border-t border-charcoal/10"
      aria-labelledby="discover-heading"
    >
      <div className="container-custom">
        <div className="max-w-2xl mx-auto text-center">
          <div
            className={`opacity-0 ${isVisible ? "animate-fade-in" : ""}`}
            style={{ animationDuration: "0.6s" }}
          >
            <h2 
              id="discover-heading"
              className="text-2xl sm:text-3xl md:text-4xl font-serif font-light text-charcoal mb-6 sm:mb-8 tracking-tight leading-tight px-4 sm:px-0"
            >
              <span className="block font-normal mb-2 sm:mb-3">Are you finding success,</span> 
              <span className="relative inline-block">
                <span className="relative z-10 italic">yet still seeking meaning?</span>
                <span className="absolute bottom-0 left-0 w-full h-[6px] bg-cream/60 -z-0 transform -translate-y-2"></span>
              </span>
            </h2>
          </div>

          <div
            className={`opacity-0 ${
              isVisible ? "animate-fade-in" : ""
            }`}
            style={{ animationDelay: "200ms", animationDuration: "0.7s" }}
          >
            <p className="text-charcoal/80 leading-relaxed mb-6 sm:mb-8 px-4 sm:px-0">
              <strong className="font-medium text-charcoal block text-xl mb-3">This conversation is not for everyone.</strong> 
              I work with a limited number of accomplished leaders who sense there's something deeper beyond their current achievements.
              If the question above resonates with you, I invite you to a private conversation to explore how we might illuminate what your success has left in the shadow.
            </p>

            <div className="mb-10 md:mb-12 bg-white/50 p-6 sm:p-8 rounded-sm shadow-sm border border-charcoal/5 mx-3 sm:mx-0">
              <div className="flex items-center justify-center mb-6">
                <div className="w-12 h-[1px] bg-charcoal/20"></div>
                <p className="text-charcoal/90 font-medium text-lg mx-3">
                  If you aspire to greater:
                </p>
                <div className="w-12 h-[1px] bg-charcoal/20"></div>
              </div>
              
              <ul className="space-y-3 text-left max-w-md mx-auto mb-8">
                {listItems.map((item, index) => (
                  <li 
                    key={index} 
                    className={`flex items-start transition-all duration-500 ease-in-out group ${
                      activeItemIndex >= index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                    }`}
                  >
                    <ChevronRight 
                      className={`h-4 w-4 mr-2 mt-1 text-charcoal/60 transition-all duration-300 flex-shrink-0 group-hover:text-monasticGold group-hover:translate-x-1 ${
                        activeItemIndex >= index ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <span 
                      className={`transition-all duration-500 delay-150 group-hover:text-charcoal/90 group-hover:-translate-y-0.5 ${
                        activeItemIndex >= index ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
              
              <div className="px-3 py-2 bg-charcoal/5 rounded-sm mb-6 text-sm text-charcoal/80 mx-auto max-w-sm">
                <p className="font-medium italic">
                  *Limited availability — Only accepting a few more conversation requests this month*
                </p>
              </div>
              
              <div className="relative">
                <a 
                  href="https://tommyzee.typeform.com/intro" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={`inline-flex items-center border bg-charcoal text-white px-6 sm:px-8 py-3 text-sm uppercase tracking-widest transition-all duration-300 relative overflow-hidden group
                    ${isButtonHovered ? 'border-cream shadow-lg translate-y-[-2px]' : 'border-charcoal/80 shadow-md'}`}
                  onMouseEnter={() => setIsButtonHovered(true)}
                  onMouseLeave={() => setIsButtonHovered(false)}
                  aria-label="Request Conversation for The Monastery Method"
                >
                  <span className="relative z-10 mr-1">Start the Conversation Now</span>
                  <ArrowRight 
                    className={`h-4 w-4 relative z-10 transition-transform duration-300 ${isButtonHovered ? 'translate-x-1' : ''}`}
                  />
                  <span className={`absolute inset-0 bg-gradient-to-r from-charcoal to-charcoal/80 ${isButtonHovered ? 'opacity-100' : 'opacity-0'} transition-opacity duration-500`}></span>
                  <span className={`absolute bottom-0 left-0 w-full h-[2px] bg-cream/30 ${isButtonHovered ? 'opacity-70' : 'opacity-0'} transition-opacity duration-300`}></span>
                </a>
                <span className={`absolute top-0 right-0 w-2 h-2 rounded-full bg-monasticGold/80 transform translate-x-1 -translate-y-1 ${isVisible ? 'animate-pulse' : 'opacity-0'}`}></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
